import React from 'react';

const LAYOUT_SHELL_READY_ATTR = 'data-layout-shell-ready';
const ROUTE_CONTENT_READY_ATTR = 'data-route-content-ready';
const ROUTE_CONTENT_FAILED_ATTR = 'data-route-content-failed';
const PRO_LAYOUT_READY_SELECTORS = [
  '.ant-pro-layout .ant-layout-sider',
  '.ant-pro-sider.ant-layout-sider',
  '.ant-pro-layout .ant-pro-layout-content',
];
const useClientLayoutEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;

function getCurrentPathname(): string {
  if (typeof window === 'undefined') {
    return '/';
  }

  return window.location.pathname || '/';
}

/**
 * 当真实业务内容已经至少成功渲染过一次后，
 * 全局 `src/loading.tsx` 再次出现时就不应立即盖回整页骨架。
 *
 * 这里给“当前路径已完成首屏渲染”建立一个当前 document 生命周期内的轻量标记：
 * - 冷启动阶段：当前路径还没有 ready 标记，全局骨架允许显示，避免白屏；
 * - 同一路径已经出过真实内容后：再触发高层 fallback 时改成延迟显示，吞掉短暂闪态；
 * - 切到新路径时：旧路径的 ready 不能污染新路径，否则会把新页面误判成“重入加载”。
 */
export const ROUTE_LOADING_REENTRY_DELAY_MS = 180;

/**
 * 读取当前文档是否已经挂起过 ProLayout 外壳。
 *
 * 这个标记和“页面真实内容是否已经就绪”是两层不同语义：
 * - layout shell ready：左侧导航 / 外层容器已经可用；
 * - route content ready：当前路径的真实内容至少出现过一次。
 *
 * `src/loading.tsx` 需要靠这个标记判断：
 * - 若外壳尚未出现，允许渲染“side skeleton + 右侧业务骨架”；
 * - 若外壳已经出现，只能渲染右侧内容骨架，避免在内容区再嵌一层假 sider。
 */
export function hasLayoutShellReady(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  /**
   * 这里不能只依赖 React effect 打的 body 标记。
   *
   * 因为在某些极短窗口里：
   * - ProLayout 左侧导航已经真实挂进 DOM；
   * - 但 marker 对应的 effect 还没来得及执行；
   * - `src/loading.tsx` 会误以为“layout 还没挂起”，于是短暂回退到整页骨架。
   *
   * 所以这里增加同步 DOM 探测：
   * 只要真实的 ProLayout sider / content 节点已经存在，
   * 就立刻判定 layout shell ready，阻止右侧再闪出一层假 sider。
   */
  if (document.body?.getAttribute(LAYOUT_SHELL_READY_ATTR) === 'true') {
    return true;
  }

  return PRO_LAYOUT_READY_SELECTORS.some((selector) => Boolean(document.querySelector(selector)));
}

/**
 * 在 ProLayout 外壳首次挂载后记录标记。
 */
export function markLayoutShellReady(): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.body?.setAttribute(LAYOUT_SHELL_READY_ATTR, 'true');
}

/**
 * 读取当前路径是否已经出现过真实业务内容。
 */
export function hasRouteContentReady(pathname: string = getCurrentPathname()): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  return document.body?.getAttribute(ROUTE_CONTENT_READY_ATTR) === pathname;
}

/**
 * 读取当前路径是否已经在首屏阶段明确失败。
 *
 * 这个标记与 ready 并列，语义都是“当前路由已经结束等待”：
 * - ready：真实页面已经稳定挂载；
 * - failed：首屏阶段出现未捕获异常，不能再继续无限等待 skeleton。
 */
export function hasRouteContentFailed(pathname: string = getCurrentPathname()): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  return document.body?.getAttribute(ROUTE_CONTENT_FAILED_ATTR) === pathname;
}

/**
 * 对全局 loading 来说，“已成功显示页面”和“已明确失败”都属于 settled。
 *
 * 只有这样，启动骨架与 `src/loading.tsx` 才不会在异常场景下无限悬挂。
 */
export function hasRouteContentSettled(pathname: string = getCurrentPathname()): boolean {
  return hasRouteContentReady(pathname) || hasRouteContentFailed(pathname);
}

/**
 * 在真实业务页面首次稳定挂载后记录就绪标记。
 *
 * 这个标记不会跨刷新持久化，只服务当前 document 生命周期，
 * 用来区分“当前路径首屏”与“同一路径已展示后又发生的短暂 suspense”。
 */
export function markRouteContentReady(pathname: string = getCurrentPathname()): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.body?.setAttribute(ROUTE_CONTENT_READY_ATTR, pathname);
  /**
   * 同一路径一旦已经成功挂载，就应清理之前的 failed 标记。
   *
   * 这样后续同路径的正常重试不会一直背着旧失败态。
   */
  if (document.body?.getAttribute(ROUTE_CONTENT_FAILED_ATTR) === pathname) {
    document.body?.removeAttribute(ROUTE_CONTENT_FAILED_ATTR);
  }
}

/**
 * 在首屏阶段遇到未捕获异常时记录失败标记。
 *
 * 注意这里只负责结束“无限等待”的骨架链路，
 * 不替代页面自己的局部 error 展示。
 */
export function markRouteContentFailed(pathname: string = getCurrentPathname()): void {
  if (typeof document === 'undefined') {
    return;
  }

  /**
   * 如果当前路径已经成功 ready，就不再把它反写成 failed。
   *
   * 这样可以避免页面已经可见后，某个后续异步错误又污染首屏判定语义。
   */
  if (document.body?.getAttribute(ROUTE_CONTENT_READY_ATTR) === pathname) {
    return;
  }

  document.body?.setAttribute(ROUTE_CONTENT_FAILED_ATTR, pathname);
}

/**
 * ProLayout 外壳挂起后立即打点。
 *
 * 这个 marker 只回答“layout 在不在”，不回答“页面数据是不是已经准备好”。
 */
export const LayoutShellReadyMarker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  /**
   * 这里使用 layout effect，让“layout 已挂起”的标记尽量发生在浏览器绘制前。
   *
   * 即便如此，`src/loading.tsx` 仍保留 DOM 探测兜底，
   * 共同消除“左侧真实 sider 已出现，但右侧误闪整页骨架”的一帧竞争态。
   */
  useClientLayoutEffect(() => {
    markLayoutShellReady();
  }, []);

  return <>{children}</>;
};

/**
 * `childrenRender` 会包裹真实路由内容，因此这里是最稳定的打点位置：
 * - 不要求各业务页自己再额外维护一套“页面已渲染”状态；
 * - 也避免把全局 loading 的抑制逻辑散落到多个页面。
 */
export const RouteContentReadyMarker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pathname = getCurrentPathname();

  React.useEffect(() => {
    markRouteContentReady(pathname);
  }, [pathname]);

  return <>{children}</>;
};

/**
 * 某些首屏异常不是 React render throw，而是 effect / Promise rejection：
 * - 例如首次请求被拒绝；
 * - 某个首屏依赖在异步初始化时抛出未处理异常。
 *
 * 如果不监听这些全局错误，骨架层只会一直等 ready，
 * 最终表现就是“页面已经失败了，但 skeleton 永远不退”。
 *
 * 这里仅在“当前路径尚未 settled”时记录 failed，
 * 不会影响页面已经显示后的后续局部异常。
 */
export const RouteFailureMarker: React.FC<{ pathname: string; children: React.ReactNode }> = ({
  pathname,
  children,
}) => {
  useClientLayoutEffect(() => {
    const markFailedIfUnsettled = () => {
      if (!hasRouteContentSettled(pathname)) {
        markRouteContentFailed(pathname);
      }
    };

    const handleWindowError = () => {
      markFailedIfUnsettled();
    };

    const handleUnhandledRejection = () => {
      markFailedIfUnsettled();
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [pathname]);

  return <>{children}</>;
};

interface RouteRenderBoundaryProps {
  pathname: string;
  children: React.ReactNode;
}

interface RouteRenderBoundaryState {
  hasError: boolean;
}

/**
 * 兜住“真实路由内容在首次渲染阶段直接 throw”的场景。
 *
 * 这是启动骨架最容易卡死的分支：
 * - 子树抛错；
 * - `RouteContentReadyMarker` 来不及打 ready；
 * - 上层 loading 仍然误以为页面在等待中。
 *
 * 因此 boundary 除了渲染降级 UI，还必须同步写入 failed marker，
 * 让全局骨架链路自动退场。
 */
export class RouteRenderBoundary extends React.Component<
  RouteRenderBoundaryProps,
  RouteRenderBoundaryState
> {
  state: RouteRenderBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RouteRenderBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    markRouteContentFailed(this.props.pathname);
    console.error('[RouteRenderBoundary] Route render failed:', error, errorInfo);
  }

  componentDidUpdate(prevProps: Readonly<RouteRenderBoundaryProps>): void {
    /**
     * boundary 是全局常驻的；当路由切换后，必须清空上一页的错误状态。
     *
     * 否则旧页面的 fallback 会污染新路径，导致新页面永远无法重新尝试挂载。
     */
    if (prevProps.pathname !== this.props.pathname && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  handleReload = (): void => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: '#f5f7fa',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            width: 'min(460px, 100%)',
            padding: '28px 24px',
            borderRadius: 20,
            border: '1px solid #e8edf3',
            background: '#ffffff',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
          }}
        >
          <div style={{ fontSize: 20, lineHeight: '28px', fontWeight: 600, color: '#1f2937' }}>
            页面加载失败
          </div>
          <div style={{ marginTop: 10, fontSize: 14, lineHeight: '22px', color: '#667085' }}>
            当前页面在首次渲染时发生异常，已自动结束骨架等待。您可以刷新页面后重试。
          </div>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              marginTop: 18,
              height: 40,
              padding: '0 16px',
              borderRadius: 10,
              border: 'none',
              background: '#1677ff',
              color: '#ffffff',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            刷新重试
          </button>
        </div>
      </div>
    );
  }
}
